import { AnimatePresence, motion } from "motion/react";

import { JSX, forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import MovieCardContent from "@/components/cards/MovieCard/MovieCardContent.tsx";

import { Content } from "@/@types/general.ts";
import { ContentHistory } from "@/api/feed/feed.types.ts";
import { determinePreviewTransform } from "@/utils/funcs.ts";

import MovieCardPreview from "./MovieCardPreview";

export type MovieCardProps = Omit<Content, "is_soon"> & Partial<Pick<ContentHistory, "time_code" | "duration">>;

const MovieCard = forwardRef<HTMLImageElement, MovieCardProps>((props, ref): JSX.Element => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [cardProperty, setCartProperty] = useState<{
    width: number;
    position: "left" | "center" | "right";
  }>({
    width: 0,
    position: "center",
  });
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "auto",
    modifiers: [
      { name: "flip", enabled: false },
      {
        name: "preventOverflow",
        options: {
          mainAxis: false,
          rootBoundary: "viewport",
          tether: false,
        },
      },
    ],
  });

  const timer = useRef<NodeJS.Timeout | null>(null);

  const showPreview = () => {
    const isDesktop = window.innerWidth > 1024;
    if (!isDesktop) return;
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      const position = determinePreviewTransform(referenceElement);
      const width = referenceElement?.offsetWidth || 0;
      setCartProperty({ width, position });

      setIsHovered(true);
    }, 500);
  };

  const hidePreview = () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      setIsHovered(false);
    }, 300);
  };

  const scrollEvent = () => {
    if (isHovered) {
      timer.current = setTimeout(() => {
        setIsHovered(false);
      }, 300);
    } else {
      if (timer.current) clearTimeout(timer.current);
    }
  };

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", scrollEvent);

    return () => {
      window.removeEventListener("scroll", scrollEvent);
    };
  }, [isHovered]);

  return (
    <div className="group relative">
      <MovieCardContent
        {...props}
        showPreview={showPreview}
        hidePreview={hidePreview}
        setReferenceElement={setReferenceElement}
        ref={ref}
      />

      {createPortal(
        <AnimatePresence mode="wait">
          {isHovered && (
            <div
              onMouseOver={() => (timer.current ? clearTimeout(timer.current) : null)}
              onMouseOut={hidePreview}
              style={styles.popper}
              ref={setPopperElement}
              {...attributes.popper}
              className={"z-[100]"}
            >
              <motion.div
                initial={{ scale: 1 }}
                exit={{ scale: 1 }}
                transition={{ ease: "easeIn", duration: 0.24 }}
                animate={{ scale: 1.4, transformOrigin: cardProperty.position }}
              >
                <MovieCardPreview width={cardProperty.width} id={props.id} previewImg={props.poster_h.small} />
              </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
});

MovieCard.displayName = "MovieCard";

export default MovieCard;
